import {
  describeApprovalStatus,
  describeMissingField,
  fetchHouseIdentity,
  fetchRegistrationState,
  houseLabel,
  submitRegistration,
  validateRegistrationDetails,
  type ApprovalStatus,
  type DetailsField,
  type RegistrationDetails,
} from '@auctisync/api-client';
import type { Tables } from '@auctisync/shared';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AddCardModal, startAddCard, type AddCardContext } from '../../components/AddCard';
import { EmptyState, GuestGate } from '../../components/bidder-web';
import { useBidderHouses } from '../../hooks/useBidderHouses';
import { useSession } from '../../hooks/useSession';
import { invokeEF } from '../../lib/edgeFunctions';
import { supabase } from '../../lib/supabase';

const STEPS = ['details', 'card', 'review'] as const;
type Step = (typeof STEPS)[number];

const EMPTY: RegistrationDetails = {
  displayName: '',
  phone: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  region: '',
  postalCode: '',
};

type Sale = Pick<
  Tables<'auctions'>,
  'id' | 'slug' | 'title' | 'tenant_id' | 'requires_bidder_approval' | 'requires_cc_preauth'
>;

/**
 * Register to bid — details, card, review.
 *
 * NEW PAGE (web design handoff, `web/web-register.jsx`). The website had no
 * registration flow at all: a bidder created an AuctiSync account, then
 * discovered on a lot page that the house also had to approve them, and typed
 * a note into a box beside the bid button. The phone has had this flow since
 * the onboarding build; this is its web half, on the SAME shared functions —
 * `validateRegistrationDetails`, `submitRegistration`, `describeApprovalStatus`
 * — so a bidder is asked for the same fields and told the same things.
 *
 * 🔴 APPROVAL IS A STATE, NOT A FOURTH STEP. The bidder does nothing during it,
 * and a step they cannot act on reads as a task they have failed to complete.
 *
 * THE RAIL IS THE POINT OF THE LAYOUT. Registration abandonment here is mostly
 * people unsure whether they are about to be charged, so "you are not charged
 * today" stays on screen at every step rather than waiting until the card step
 * to reassure them.
 *
 * A HOUSE THAT DOES NOT REQUIRE APPROVAL GETS NO FLOW. `fetchRegistrationState`
 * reads the requirement as well as the request, because "no approval row" means
 * two completely different things depending on it — nothing to do, or nothing
 * done yet. Showing a registration form where none is required invents friction
 * the house deliberately did not ask for.
 */
export function PublicRegister() {
  const [params] = useSearchParams();
  const { session, userId } = useSession();
  const { selected, active, discoverHouse } = useBidderHouses();

  const [sale, setSale] = useState<Sale | null>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState<Step>('details');
  const [details, setDetails] = useState<RegistrationDetails>(EMPTY);
  const [note, setNote] = useState('');
  const [showErrors, setShowErrors] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [status, setStatus] = useState<ApprovalStatus>('none');
  const [required, setRequired] = useState(true);
  const [hasCard, setHasCard] = useState(false);
  const [addContext, setAddContext] = useState<AddCardContext | null>(null);
  // Held across a retry so two racing clicks dedupe to one SetupIntent.
  const nonceRef = useRef<string | null>(null);

  const saleParam = params.get('sale');

  // ── which sale are we registering FOR ─────────────────────────────
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    void (async () => {
      let query = supabase
        .from('auctions')
        .select('id, slug, title, tenant_id, requires_bidder_approval, requires_cc_preauth')
        .eq('is_visible', true)
        .in('status', ['active', 'preview'])
        .order('ends_at', { ascending: true, nullsFirst: false })
        .limit(saleParam === null ? 1 : 50);
      if (selected) query = query.eq('tenant_id', selected.tenant_id);

      const { data } = await query;
      if (cancelled) return;
      const rows = (data ?? []) as Sale[];
      const chosen = rows.find((s) => s.slug === saleParam) ?? rows[0] ?? null;
      setSale(chosen);
      setLoading(false);
      if (chosen) {
        void fetchHouseIdentity(supabase, chosen.tenant_id).then((house) => {
          if (!cancelled) discoverHouse(house);
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selected, saleParam, discoverHouse]);

  // ── where this bidder already stands, and their details ───────────
  useEffect(() => {
    if (userId === null || sale === null) return;
    let cancelled = false;
    void (async () => {
      const [state, { data: profile }, cards] = await Promise.all([
        fetchRegistrationState(supabase, { auctionId: sale.id, userId }),
        supabase
          .from('profiles')
          .select('display_name, phone, address_line1, address_line2, city, region, postal_code')
          .eq('id', userId)
          .maybeSingle(),
        invokeEF<{ payment_methods?: unknown[] }>('list-payment-methods', {
          tenant_id: sale.tenant_id,
        }),
      ]);
      if (cancelled) return;

      if (state.ok) {
        setStatus(state.state.approvalStatus);
        setRequired(state.state.required);
      }
      // Pre-filled from the profile, because nothing should be asked twice. A
      // bidder registering with a second house has already told us their name.
      if (profile) {
        setDetails({
          displayName: profile.display_name ?? '',
          phone: profile.phone ?? '',
          addressLine1: profile.address_line1 ?? '',
          addressLine2: profile.address_line2 ?? '',
          city: profile.city ?? '',
          region: profile.region ?? '',
          postalCode: profile.postal_code ?? '',
        });
      }
      setHasCard((cards.data?.payment_methods ?? []).length > 0);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, sale]);

  const missing = useMemo(() => validateRegistrationDetails(details), [details]);
  const houseName = active === null ? 'the auction house' : houseLabel(active);
  const index = STEPS.indexOf(step);

  function set(field: DetailsField) {
    return (value: string) => setDetails((d) => ({ ...d, [field]: value }));
  }

  async function beginAddCard() {
    if (sale === null) return;
    nonceRef.current ??= crypto.randomUUID();
    setBusy(true);
    const res = await startAddCard({
      tenantId: sale.tenant_id,
      tenantName: houseName,
      nonce: nonceRef.current,
    });
    setBusy(false);
    if (!res.ok) {
      setMessage(res.message);
      return;
    }
    nonceRef.current = null;
    setAddContext(res.context);
  }

  async function send() {
    if (!session || sale === null) return;
    setBusy(true);
    setMessage(null);
    const res = await submitRegistration(supabase, {
      tenantId: sale.tenant_id,
      auctionId: sale.id,
      userId: session.user.id,
      details,
      note,
    });
    setBusy(false);
    if (!res.ok) {
      setMessage(res.message);
      return;
    }
    setStatus('pending');
  }

  if (loading) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <p className="muted">Loading…</p>
      </div>
    );
  }

  if (sale === null) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <EmptyState
          icon="⏱"
          title="No sale is open to register for"
          body="Registration is per sale, so there has to be one running. Create an account now and it will be ready when the next sale opens."
          action={
            <Link to={session ? '/auctions' : '/signup'} className="w-btn w-btn-cta">
              {session ? 'See all auctions' : 'Create an account'}
            </Link>
          }
        />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <GuestGate
          title={`Registering with ${houseName} starts with an account`}
          body="One AuctiSync account works with every house on the platform. You register with each house separately — they approve their own bidders."
          secondaryLabel="Create an account"
          secondaryTo="/signup"
        />
      </div>
    );
  }

  // ── the states that replace the form ──────────────────────────────
  if (!required) {
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <EmptyState
          icon="✓"
          title={`${houseName} does not require approval for this sale`}
          body="You can bid straight away. Some sales still ask for a card on file before the first bid — the lot page will say so if this one does."
          action={
            <Link to={`/browse?sale=${sale.slug}`} className="w-btn w-btn-cta">
              Browse the lots
            </Link>
          }
        />
      </div>
    );
  }

  if (status !== 'none') {
    const described = describeApprovalStatus(status, active === null ? null : houseLabel(active));
    return (
      <div className="w-wrap" style={{ padding: '48px 0' }}>
        <EmptyState
          icon={described.tone === 'success' ? '✓' : described.tone === 'error' ? '!' : '⏱'}
          title={described.title}
          body={described.body}
          action={
            <Link
              to={status === 'approved' ? `/browse?sale=${sale.slug}` : '/browse'}
              className="w-btn w-btn-cta"
            >
              {status === 'approved' ? 'Browse the lots' : 'Browse while you wait'}
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className="w-wrap">
      <div style={{ paddingTop: 40 }}>
        <h1 className="w-h1">Register to bid</h1>
        <p className="w-body muted" style={{ marginTop: 10, maxWidth: 560 }}>
          {houseName} approves its own bidders. This goes to them, once, for {sale.title}.
        </p>
      </div>

      <div className="wa-reg">
        <div>
          <div className="wa-steps">
            {STEPS.map((s, i) => (
              <div key={s} className="wa-step" data-on={step === s}>
                <span className="wa-dot">{i + 1}</span>
                {s === 'details'
                  ? 'Your details'
                  : s === 'card'
                    ? 'A card on file'
                    : 'Check and send'}
                {i < STEPS.length - 1 ? (
                  <span aria-hidden className="muted">
                    ›
                  </span>
                ) : null}
              </div>
            ))}
          </div>

          {step === 'details' ? (
            <div className="wa-form">
              <Field
                label="Name the house should know you by"
                value={details.displayName}
                onChange={set('displayName')}
                autoComplete="name"
              />
              <Field
                label="Phone"
                value={details.phone}
                onChange={set('phone')}
                autoComplete="tel"
                inputMode="tel"
              />
              <Field
                label="Billing address"
                value={details.addressLine1}
                onChange={set('addressLine1')}
                autoComplete="address-line1"
              />
              <Field
                label="Apartment, suite (optional)"
                value={details.addressLine2}
                onChange={set('addressLine2')}
                autoComplete="address-line2"
              />
              <div className="wa-two">
                <Field
                  label="City"
                  value={details.city}
                  onChange={set('city')}
                  autoComplete="address-level2"
                />
                <Field
                  label="State or region"
                  value={details.region}
                  onChange={set('region')}
                  autoComplete="address-level1"
                />
              </div>
              <Field
                label="Postal code"
                value={details.postalCode}
                onChange={set('postalCode')}
                autoComplete="postal-code"
                inputMode="numeric"
              />
              {showErrors && missing.length > 0 ? (
                <p
                  role="alert"
                  style={{ color: 'var(--semantic-error)', fontSize: 13.5, margin: 0 }}
                >
                  {describeMissingField(missing[0] as DetailsField)}
                </p>
              ) : null}
            </div>
          ) : null}

          {step === 'card' ? (
            <div className="wa-form">
              <h2 className="w-h2" style={{ fontSize: 20 }}>
                A card on file
              </h2>
              {/*
                SAID HERE, ONCE, WHERE THE CARD IS ASKED FOR — not on the lot
                page, and never framed as a per-lot or per-auction hold, because
                it is neither. Explaining it at the moment somebody is asked for
                a card is what stops them abandoning.
              */}
              <p className="w-body">
                {sale.requires_cc_preauth
                  ? `${houseName} verifies your card once, for a dollar, and releases it straight away. You are only charged for lots you win, on the invoice after the sale.`
                  : `${houseName} does not ask for a card up front for this sale. You can still add one now so paying an invoice later is one click.`}
              </p>
              {hasCard ? (
                <p className="muted" style={{ margin: 0 }}>
                  You already have a card on file with {houseName} — nothing to do here.
                </p>
              ) : (
                <button
                  type="button"
                  className="w-btn w-btn-out"
                  disabled={busy}
                  onClick={() => void beginAddCard()}
                  style={{ justifySelf: 'start' }}
                >
                  {busy ? 'Opening…' : 'Add a card'}
                </button>
              )}
            </div>
          ) : null}

          {step === 'review' ? (
            <div className="wa-form">
              <h2 className="w-h2" style={{ fontSize: 20 }}>
                Check and send
              </h2>
              {/* The moment a stranger's details go to a business. They should
                  see exactly what is being sent, before it is sent. */}
              <div
                style={{
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--r-card)',
                  padding: '4px 18px',
                }}
              >
                <Row label="Name" value={details.displayName} />
                <Row label="Phone" value={details.phone} />
                <Row
                  label="Address"
                  value={[
                    details.addressLine1,
                    details.addressLine2,
                    details.city,
                    details.region,
                    details.postalCode,
                  ]
                    .filter((part) => part.trim() !== '')
                    .join(', ')}
                />
                <Row label="Card" value={hasCard ? 'On file' : 'Not added'} />
              </div>
              <label className="wa-fi">
                <span className="w-lbl">Anything the house should know (optional)</span>
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} />
              </label>
              <p className="muted" style={{ fontSize: 12.5, margin: 0, lineHeight: 1.55 }}>
                These details go to {houseName} so they can invoice and release lots to the right
                person. They are not shown to other bidders.
              </p>
              {message !== null ? (
                <p
                  role="alert"
                  style={{ color: 'var(--semantic-error)', fontSize: 13.5, margin: 0 }}
                >
                  {message}
                </p>
              ) : null}
            </div>
          ) : null}

          <div style={{ display: 'flex', gap: 10, marginTop: 26, flexWrap: 'wrap' }}>
            {index > 0 ? (
              <button
                type="button"
                className="w-btn w-btn-out"
                onClick={() => setStep(STEPS[index - 1] as Step)}
              >
                Back
              </button>
            ) : null}
            <button
              type="button"
              className="w-btn w-btn-cta"
              disabled={busy}
              onClick={() => {
                if (step === 'details') {
                  setShowErrors(true);
                  if (missing.length === 0) {
                    setShowErrors(false);
                    setStep('card');
                  }
                  return;
                }
                if (step === 'card') {
                  setStep('review');
                  return;
                }
                void send();
              }}
            >
              {step === 'review' ? (busy ? 'Sending…' : 'Send registration') : 'Continue'}
            </button>
          </div>
        </div>

        <aside className="wa-side">
          <div className="wl-panel">
            <div className="wl-ph">
              <span className="w-lbl">What happens next</span>
            </div>
            <div style={{ padding: '4px 14px 14px' }}>
              <ol
                className="w-body muted"
                style={{ fontSize: 13.5, paddingLeft: 18, margin: '10px 0 0', lineHeight: 1.6 }}
              >
                <li>{houseName} reviews your details.</li>
                <li>You are told when they approve you — usually within a few hours.</li>
                <li>Then you can bid. Watching works while you wait.</li>
              </ol>
            </div>
          </div>

          <div className="wl-panel">
            <div className="wl-ph">
              <span className="w-lbl">You are not charged today</span>
            </div>
            <p className="muted" style={{ fontSize: 13, padding: 14, margin: 0, lineHeight: 1.55 }}>
              A card on file is verified once, for a dollar, and released. You are billed after the
              sale, for the lots you actually won.
            </p>
          </div>
        </aside>
      </div>

      {addContext !== null ? (
        <AddCardModal
          context={addContext}
          onClose={() => setAddContext(null)}
          onSaved={() => {
            setAddContext(null);
            setHasCard(true);
          }}
        />
      ) : null}
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  autoComplete,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete?: string;
  inputMode?: 'tel' | 'numeric';
}) {
  return (
    <label className="wa-fi">
      <span className="w-lbl">{label}</span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        {...(autoComplete === undefined ? {} : { autoComplete })}
        {...(inputMode === undefined ? {} : { inputMode })}
      />
    </label>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="w-kv">
      <span className="muted">{label}</span>
      <span>{value === '' ? '—' : value}</span>
    </div>
  );
}
